import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SeoHead } from "@/components/common/SeoHead";
import { WhatsAppCTA } from "@/components/common/WhatsAppCTA";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, Phone, MapPin, Globe, Users, Star, Heart, Headphones } from "lucide-react";

const BREADCRUMB = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://tripsyfy.com" },
    { "@type": "ListItem", position: 2, name: "About Tripsyfy", item: "https://tripsyfy.com/about" },
  ],
});

const FAQ_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "What is Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "Tripsyfy is a US-based travel solutions company that helps individuals, families, and groups book international flights, guided tours, cruise packages, and custom itineraries. We provide human support throughout the entire planning and booking process." } },
    { "@type": "Question", name: "Where is Tripsyfy based?", acceptedAnswer: { "@type": "Answer", text: "Tripsyfy is based in Edison, New Jersey, and serves clients across the United States. We specialize in helping US-based travelers — including families, diaspora communities, and first-time international travelers — plan and book trips around the world." } },
    { "@type": "Question", name: "Is Tripsyfy a real travel agency?", acceptedAnswer: { "@type": "Answer", text: "Yes. Tripsyfy is a legitimate travel solutions company providing personalized booking assistance for flights, tours, cruises, and custom trips. We work with real suppliers and carriers and provide human support throughout the booking process." } },
    { "@type": "Question", name: "How is Tripsyfy different from online booking sites?", acceptedAnswer: { "@type": "Answer", text: "Unlike automated booking sites, Tripsyfy provides real human support. Our travel experts help you compare options, navigate complex itineraries, and handle the details — especially for international travel, family trips, group travel, and routes that online tools handle poorly." } },
    { "@type": "Question", name: "How do I contact Tripsyfy?", acceptedAnswer: { "@type": "Answer", text: "You can reach Tripsyfy by phone at +1 972-666-1122, by email at hello@tripsyfy.com, by WhatsApp at the same number, or by submitting a request through the contact form at tripsyfy.com/contact." } },
    { "@type": "Question", name: "Does Tripsyfy charge for consultations?", acceptedAnswer: { "@type": "Answer", text: "Initial consultations and quote requests are free. We discuss your travel needs, compare available options, and only proceed to booking when you're satisfied with the plan." } },
    { "@type": "Question", name: "What types of trips does Tripsyfy handle?", acceptedAnswer: { "@type": "Answer", text: "Tripsyfy handles international and domestic flights, guided group tours, cruise bookings, custom multi-city itineraries, group travel coordination, and honeymoon packages. We specialize in complex trips that are difficult to book through online tools." } },
  ],
});

const services = [
  { icon: Globe, label: "International Flights", path: "/international-flight-booking" },
  { icon: MapPin, label: "Flights to India from USA", path: "/flights-to-india-from-usa" },
  { icon: MapPin, label: "Flights to Africa from USA", path: "/flights-to-africa-from-usa" },
  { icon: Heart, label: "Guided Tour Packages", path: "/tours" },
  { icon: Users, label: "Group Travel Planning", path: "/group-travel-planning" },
  { icon: Star, label: "Cruise Booking Assistance", path: "/cruise-booking-assistance" },
  { icon: Heart, label: "Custom Tour Packages", path: "/custom-tour-packages" },
  { icon: Globe, label: "Polar Expeditions", path: "/expeditions/antarctica" },
];

const whyPoints = [
  "Human support from first call to final boarding pass",
  "Expert guidance for complex, multi-city, and international routes",
  "Specialists in family travel, group bookings, and diaspora travel",
  "No hidden fees — transparent pricing at every step",
  "Personalized options based on your budget and schedule",
  "Responsive communication via phone, email, and WhatsApp",
];

const faqs = [
  { q: "What is Tripsyfy?", a: "Tripsyfy is a US-based travel solutions company that helps individuals, families, and groups book international flights, guided tours, cruise packages, and custom itineraries with personalized human support." },
  { q: "Where is Tripsyfy based?", a: "Tripsyfy is based in Edison, New Jersey, and serves clients across the United States. We specialize in helping US-based travelers plan and book international trips." },
  { q: "Is Tripsyfy a real travel agency?", a: "Yes. Tripsyfy is a legitimate travel solutions company providing personalized booking assistance for flights, tours, cruises, and custom trips. We work with real suppliers and provide human support throughout." },
  { q: "How is Tripsyfy different from online booking sites?", a: "Unlike automated booking sites, Tripsyfy provides real human support. Our travel experts help you compare options, navigate complex itineraries, and handle the details — especially for international travel, family trips, and group travel." },
  { q: "How do I contact Tripsyfy?", a: "You can reach us by phone at +1 972-666-1122, by email at hello@tripsyfy.com, by WhatsApp, or through the contact form on our website." },
  { q: "Does Tripsyfy charge for consultations?", a: "Initial consultations and quote requests are completely free. We discuss your travel needs, compare options, and only proceed to booking when you're ready." },
  { q: "What types of trips does Tripsyfy handle?", a: "We handle international flights, guided tours, cruise bookings, custom multi-city itineraries, group travel, and honeymoon packages — especially complex trips that are hard to book online." },
];

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div id="main-content" className="min-h-screen bg-white">
      <Helmet>
        <script type="application/ld+json">{BREADCRUMB}</script>
        <script type="application/ld+json">{FAQ_SCHEMA}</script>
      </Helmet>
      <SeoHead
        title="About Tripsyfy | Personal Travel Planning Support"
        description="Learn about Tripsyfy, a travel solutions company based in Edison, NJ, helping families, groups, and busy travelers simplify flights, tours, and trip planning."
        canonical="https://tripsyfy.com/about"
      />
      <Navbar />

      <section className="relative pt-28 pb-14 bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <p className="text-sky-300 text-xs font-semibold tracking-widest uppercase mb-3">Who We Are</p>
          <h1 className="heading-xl text-white mb-3">About Tripsyfy</h1>
          <p className="text-base text-white/70 max-w-xl leading-relaxed">
            A US-based travel solutions company built to make international travel simple — for families, groups, and busy travelers who want real human support, not just a booking engine.
          </p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <AnimatedSection>
              <p className="overline mb-3">Our Story</p>
              <h2 className="heading-lg text-primary mb-5">Built for travelers who want a real person in their corner</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Tripsyfy was founded with a simple belief: booking a trip — especially internationally — should come with a knowledgeable, responsive human being who actually cares about getting it right. Not a chatbot. Not an automated filter system.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We're based in Edison, New Jersey, and serve clients across the United States. Our focus is on families, diaspora communities traveling back to India and Africa, groups, and anyone planning a trip that's too important to trust to an algorithm.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                From the first call to the final boarding pass, our team handles the logistics so you can focus on the journey.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "5,000+", label: "Trips Planned" },
                  { value: "4.9/5", label: "Average Rating" },
                  { value: "50+", label: "Countries Covered" },
                  { value: "24/7", label: "Support Available" },
                ].map(({ value, label }) => (
                  <div key={label} className="bg-surface-subtle rounded-xl p-6 text-center">
                    <p className="text-3xl font-bold text-accent mb-1">{value}</p>
                    <p className="text-sm text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Why Travelers Choose Us</p>
              <h2 className="heading-lg text-primary">What makes Tripsyfy different</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyPoints.map((point, i) => (
              <AnimatedSection key={i} delay={i * 0.06}>
                <div className="bg-white rounded-xl p-5 shadow-card flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5 text-accent" />
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{point}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wide px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <p className="overline mb-3">Our Services</p>
              <h2 className="heading-lg text-primary">What we can book for you</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {services.map(({ icon: Icon, label, path }, i) => (
              <AnimatedSection key={label} delay={i * 0.05}>
                <Link to={path} className="group bg-surface-subtle rounded-xl p-5 flex flex-col items-center text-center hover:shadow-card-hover transition-shadow duration-200 gap-3">
                  <div className="h-11 w-11 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <p className="text-sm font-semibold text-primary leading-tight">{label}</p>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface-subtle">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <AnimatedSection>
            <p className="overline mb-3 text-center">Common Questions</p>
            <h2 className="heading-lg text-primary text-center mb-10">About Tripsyfy</h2>
          </AnimatedSection>
          <div className="space-y-3">
            {faqs.map(({ q, a }, i) => (
              <AnimatedSection key={i} delay={i * 0.04}>
                <details className="bg-white rounded-xl shadow-card overflow-hidden group">
                  <summary className="px-6 py-4 cursor-pointer font-semibold text-primary text-sm list-none flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    {q}
                    <span className="text-accent text-lg leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </div>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary">
        <div className="container-wide px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto text-center">
          <AnimatedSection>
            <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-3">Get in Touch</p>
            <h2 className="heading-lg text-white mb-4">Ready to start planning?</h2>
            <p className="text-white/70 text-base leading-relaxed mb-8">
              Tell us where you want to go — we'll take care of the rest. Reach us by phone, email, or WhatsApp.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button className="bg-accent text-white hover:bg-accent/90 w-full sm:w-auto">
                  Request a Free Quote
                </Button>
              </Link>
              <WhatsAppCTA variant="button" />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center text-sm text-white/60">
              <a href="tel:+19726661122" className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone className="h-4 w-4" /> +1 972 666 1122
              </a>
              <span className="hidden sm:inline">•</span>
              <a href="mailto:hello@tripsyfy.com" className="hover:text-white transition-colors">hello@tripsyfy.com</a>
              <span className="hidden sm:inline">•</span>
              <span className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Edison, NJ</span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
